import React from 'react';
import {Controller, ControllerProps, FieldPath, FieldValues, RegisterOptions, ValidationRule} from 'react-hook-form';
import AppDateTimePicker, {AppDateTimePickerProps} from './AppDateTimePicker';
import {FormHelperText} from '@mui/material';

export type AppControlledDateTimePickerProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<AppDateTimePickerProps, 'name' | 'required'> &
    Omit<ControllerProps<TFieldValues, TName>, 'render'> & {
        required?: string | ValidationRule<boolean>;
        label: string | React.ReactNode;
        errorMessage?: string;
    };

export function AppControlledDateTimePicker<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    rules,
    onChange,
    required,
    label,
    errorMessage,
    ...rest
}: AppControlledDateTimePickerProps<TFieldValues, TName>) {
    const customeRules: Omit<
        RegisterOptions<TFieldValues, TName>,
        'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'
    > = rules ? rules : {required: required ? `${label} is required` : false};
    return (
        <Controller
            name={name}
            rules={customeRules}
            control={control}
            render={({field, fieldState: {invalid, error}}) => {
                return (
                    <>
                        <AppDateTimePicker
                            label={label}
                            required={!!required}
                            {...rest}
                            {...field}
                            value={new Date(field.value)}
                            defaultValue={null}
                            onChange={(e, context) => {
                                if (onChange) {
                                    onChange(e, context);
                                }
                                field.onChange(e);
                            }}
                        />
                        <FormHelperText sx={{color: 'red'}}>{errorMessage ?? error?.message ?? ''}</FormHelperText>
                    </>
                );
            }}
        />
    );
}

export default AppControlledDateTimePicker;
